import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { App, Modal, theme } from "antd";
import classNames from "classnames";
import $ from "jquery";
import { Observer, observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { FOLDER_TREE_DATA_DEFAULTS, FOLDER_TREE_TOOLBAR_INITIAL_STATE, Spin, Tree, createObserverTreeRenderItem, useFolderTreeProvider, useFolderTreeProxyRefActions } from "@devowl-wp/react-folder-tree";
import { Breadcrumb } from "./breadcrumb.js";
import { cloneTreeNodes, createAllNode, createAuxiliaryTreeStore, createUnorganizedNode } from "../../store/treeStore.js";
import { __ } from "../../utils/i18n.js";
import { resolveIcon } from "../../utils/icons.js";
import { rmlOpts } from "../../utils/rmlOpts.js";
import { Provider as AntdProvider } from "../common/provider.js";
const FolderSelector = observer(({ tree, input, selected, disabled = "", fetch = true, nullable = false, editable = true, onSelect, onNodeInit, onFetchTree, ignoreChildNodes = false, before, after, title, className, style, children })=>{
    const { message } = App.useApp();
    const { token } = theme.useToken();
    const inputRef = useRef(input ?? document.createElement("input"));
    const suppressOnSelectRef = useRef(false);
    const nullableNextVisibleChangeRef = useRef(false);
    const [isTreeBusy, setIsTreeBusy] = useState(false);
    const [visible, setVisible] = useState(false);
    const [displayPath, setDisplayPath] = useState(undefined);
    const disabledTypes = useRef((disabled ? `${disabled}`.split(",") : []).map((i)=>+i));
    const getInputValue = useCallback(()=>{
        const val = inputRef.current.value;
        return val === "" || val === "all" ? "" : +val;
    }, []);
    const getMainStore = useCallback(()=>{
        const globalRml = window.rml;
        return globalRml?.store?.tree?.length ? globalRml.store : undefined;
    }, []);
    const onTreeNodeRender = useMemo(()=>createObserverTreeRenderItem(Observer), []);
    const onTreeNodeRenderIcon = useCallback((icon)=>resolveIcon(icon), []);
    const normalizeSelectId = useCallback((id)=>{
        if (id === "" || id === undefined || id === null) {
            return "";
        }
        return tree.normalizeTreeId(id);
    }, [
        tree
    ]);
    const handleSelect = useCallback((id)=>{
        let item;
        const normalizedId = normalizeSelectId(id);
        const previousValue = getInputValue();
        if (normalizedId === "" || normalizedId === false) {
            item = tree.selected;
            tree.setSelected(undefined);
            item = null;
        } else {
            item = tree.getTreeItemById(normalizedId, false);
            if (!item) {
                if (suppressOnSelectRef.current) {
                    inputRef.current.value = String(normalizedId);
                    return undefined;
                }
                return handleSelect(+rmlOpts.others.rootId);
            }
            tree.setSelected(item);
        }
        inputRef.current.value = normalizedId === false || normalizedId === "" ? "" : String(normalizedId);
        $(inputRef.current).data("node", item).trigger("folderSelected");
        setVisible(false);
        setDisplayPath(item ? item.path.map((node)=>node.title) : undefined);
        if (!suppressOnSelectRef.current && previousValue !== normalizedId && onSelect) {
            onSelect(item, inputRef.current);
        }
        return item;
    }, [
        getInputValue,
        normalizeSelectId,
        onSelect,
        tree
    ]);
    const handleVisibleChange = useCallback(()=>{
        let nextVisible = !visible;
        if (nullableNextVisibleChangeRef.current) {
            nextVisible = false;
        }
        setVisible(nextVisible);
        nullableNextVisibleChangeRef.current = false;
    }, [
        visible
    ]);
    const handleSelectNull = useCallback(()=>{
        nullableNextVisibleChangeRef.current = true;
        handleSelect("");
    }, [
        handleSelect
    ]);
    useEffect(()=>{
        if (!input && selected !== undefined) {
            handleSelect(selected);
        }
    }, [
        input,
        selected,
        handleSelect
    ]);
    useEffect(()=>{
        const val = getInputValue();
        if (!nullable && (!val || val < 0)) {
            inputRef.current.value = "-1";
        }
        const loadTree = async ()=>{
            setIsTreeBusy(true);
            if (fetch) {
                try {
                    const mainStore = getMainStore();
                    if (mainStore) {
                        tree.setTree(cloneTreeNodes(mainStore.tree, tree), false, mainStore.slugs);
                    } else {
                        await tree.fetchTree();
                    }
                    onFetchTree?.(tree);
                } catch (e) {
                    message.error(e instanceof Error ? e.message : __("Could not load the folder tree."));
                }
            }
            const fnDisabled = (node)=>{
                if (disabledTypes.current.indexOf(node.properties && +node.properties.type) > -1) {
                    node.setter((n)=>{
                        n.className = "rml-deactivated";
                    });
                }
            };
            tree.nodes(fnDisabled);
            tree.nodes(fnDisabled, true);
            setIsTreeBusy(false);
            suppressOnSelectRef.current = true;
            const item = handleSelect(val);
            suppressOnSelectRef.current = false;
            if (item) {
                setDisplayPath(item.path.map((node)=>node.title));
            }
            onNodeInit?.(item ?? null, inputRef.current);
        };
        loadTree();
    }, []);
    const { staticTree, tree: treeNodes } = tree;
    const callbacksRef = useRef({});
    const proxyRefActions = useFolderTreeProxyRefActions(callbacksRef);
    const [FolderTreeProvider, folderTreeCtx] = useFolderTreeProvider({
        ...FOLDER_TREE_DATA_DEFAULTS,
        ...FOLDER_TREE_TOOLBAR_INITIAL_STATE(),
        rootId: +rmlOpts.others.rootId,
        staticTree: staticTree.filter(({ id: nodeId })=>nodeId !== "all"),
        tree: treeNodes.length > 0 ? treeNodes : [],
        ignoreChildNodes,
        headline: /*#__PURE__*/ _jsx("span", {
            style: {
                paddingRight: 5
            },
            children: __("Folders")
        }),
        noFoldersTitle: __("No folders found"),
        noFoldersDescription: "",
        noSearchResult: __("No search results."),
        autoFocusSearchInput: true,
        isTreeBusy,
        creatable: {
            buttons: {},
            backButton: {
                label: __("Cancel")
            }
        },
        toolbar: {
            buttons: {},
            backButton: {
                label: __("Cancel")
            }
        }
    }, {}, proxyRefActions);
    callbacksRef.current = {
        onSelect: handleSelect,
        renderItem: onTreeNodeRender,
        renderIcon: onTreeNodeRenderIcon
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Modal, {
                open: visible && editable,
                wrapClassName: "rml-folder-selector",
                okText: __("Ok"),
                cancelText: __("Cancel"),
                title: title,
                onCancel: handleVisibleChange,
                styles: {
                    body: {
                        // @see https://github.com/ant-design/ant-design/blob/f97ec837e9187dc688de922d755e11aa8e5c06cb/components/modal/style/index.ts#L469-L471
                        marginInline: -token.paddingContentHorizontalLG
                    }
                },
                children: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(FolderTreeProvider, {
                            value: folderTreeCtx,
                            children: /*#__PURE__*/ _jsx(Tree, {
                                isResizable: false,
                                isFullWidth: true,
                                theme: "wordpress"
                            })
                        }),
                        children
                    ]
                })
            }, "modal"),
            before,
            /*#__PURE__*/ _jsx("div", {
                className: classNames("rml-folder-edit", className, {
                    "rml-deactivated": !editable
                }),
                style: style,
                onClick: handleVisibleChange,
                children: /*#__PURE__*/ _jsx(Spin, {
                    spinning: isTreeBusy,
                    children: /*#__PURE__*/ _jsx(Breadcrumb, {
                        path: displayPath ?? tree.breadcrumb,
                        closeable: !!(tree.selected && editable && nullable),
                        onClose: handleSelectNull
                    })
                })
            }, "breadcrumb"),
            after
        ]
    });
});
function createFolderSelector(target, input, props, useStore) {
    const store = useStore || (()=>{
        const aux = createAuxiliaryTreeStore();
        const all = createAllNode(aux);
        all.$visible = false;
        aux.staticTree = [
            all,
            createUnorganizedNode(aux)
        ];
        return aux;
    })();
    createRoot(target).render(/*#__PURE__*/ _jsx(AntdProvider, {
        children: /*#__PURE__*/ _jsx(FolderSelector, {
            tree: store,
            input: input,
            ...props
        })
    }));
    return store;
}
export { FolderSelector, createFolderSelector };
